import { ApiError } from './api.ts';
import { locale, t } from '../i18n/index.ts';
import { cleanId, idError, idLooksRight, type CountryRules } from './country.ts';
import type { Company, Receipt } from './types.ts';

/**
 * The business details form, kept apart from the page so its rules can be tested on their own.
 * The server (packages/core/src/company.ts) checks the same things; these checks only say it
 * sooner, next to the field.
 */
export type BusinessForm = Record<
  | 'legal_name'
  | 'trade_name'
  | 'tax_id'
  | 'vat_id'
  | 'reg_no'
  | 'einvoice_id'
  | 'address'
  | 'city'
  | 'postcode'
  | 'country'
  | 'region'
  | 'phone'
  | 'email'
  | 'website'
  | 'receipt_footer'
  | 'language'
  | 'paper',
  string
>;
export type BusinessField = keyof BusinessForm;
export type BusinessErrors = Partial<Record<BusinessField, string>>;

/** Longest text the server keeps for each field. */
export const LIMITS: Record<BusinessField, number> = {
  legal_name: 120,
  trade_name: 120,
  tax_id: 30,
  vat_id: 30,
  reg_no: 60,
  einvoice_id: 120,
  address: 160,
  city: 80,
  postcode: 12,
  country: 2,
  region: 40,
  phone: 40,
  email: 120,
  website: 120,
  receipt_footer: 300,
  language: 2,
  paper: 6,
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const toForm = (c: Company): BusinessForm => ({
  legal_name: c.legal_name,
  trade_name: c.trade_name ?? '',
  tax_id: c.tax_id ?? '',
  vat_id: c.vat_id ?? '',
  reg_no: c.reg_no ?? '',
  einvoice_id: c.einvoice_id ?? '',
  address: c.address ?? '',
  city: c.city ?? '',
  postcode: c.postcode ?? '',
  country: c.country,
  region: c.region ?? '',
  phone: c.phone ?? '',
  email: c.email ?? '',
  website: c.website ?? '',
  receipt_footer: c.receipt_footer ?? '',
  language: c.language ?? 'en',
  // Empty until saved once: the server then gives the country's paper (Letter in North America).
  paper: c.paper ?? '',
});

/** A tax number the way the server stores it: capitals, no spaces. */
export const cleanTaxId = cleanId;

/** What is sent to `company.update`. A paper not chosen yet is left to the country. */
export const toPayload = (f: BusinessForm): BusinessForm => {
  const { paper, ...rest } = f;
  return {
    ...rest,
    tax_id: cleanId(f.tax_id),
    vat_id: cleanId(f.vat_id),
    reg_no: f.reg_no.trim(),
    einvoice_id: f.einvoice_id.trim(),
    postcode: f.postcode.trim().toUpperCase(),
    country: f.country.trim().toUpperCase(),
    region: f.region.trim(),
    ...(paper ? { paper } : {}),
  } as BusinessForm;
};

/** The rules of the country in the form, once they are loaded for that country. */
const rulesFor = (f: BusinessForm, rules: CountryRules | null | undefined) => (rules && rules.code === f.country.trim().toUpperCase() ? rules : null);

/**
 * Problems the person can fix before saving, by field. The business numbers and the postcode are
 * checked by the country pack, only where their format is certain; the server checks the same.
 */
export function validate(f: BusinessForm, countryRules?: CountryRules | null): BusinessErrors {
  const e: BusinessErrors = {};
  const country = f.country.trim().toUpperCase();
  const rules = rulesFor(f, countryRules);
  if (!f.legal_name.trim()) e.legal_name = t('form.required');
  if (!/^[A-Z]{2}$/.test(country)) e.country = t('business.country_rule');
  const checks: [BusinessField, string | null][] = [
    ['tax_id', idError(rules?.taxId, f.tax_id)],
    ['vat_id', idError(rules?.vatId, f.vat_id)],
    ['reg_no', idError(rules?.regNo, f.reg_no)],
    ['postcode', idError(rules?.address.postcode, f.postcode, { keepSpaces: true })],
  ];
  for (const [k, problem] of checks) if (problem) e[k] = problem;
  if (f.email.trim() && !EMAIL.test(f.email.trim())) e.email = t('business.email_rule');
  return e;
}

/** Whether the tax number, as typed, matches its country's certain format (a quiet confirmation). */
export const taxIdLooksRight = (f: BusinessForm, rules?: CountryRules | null) => idLooksRight(rulesFor(f, rules)?.taxId, f.tax_id);

/** Two forms say the same once the server has tidied them. */
export const sameDetails = (a: BusinessForm, b: BusinessForm) => JSON.stringify(toPayload(a)) === JSON.stringify(toPayload(b));

/**
 * The server's answer to a refused save, put next to the field it names. A check the server
 * makes after reading the input (an older server) may name its field in the message only.
 */
export function serverErrors(err: unknown): BusinessErrors {
  if (!(err instanceof ApiError)) return {};
  const out: BusinessErrors = {};
  const issues = (err.details?.issues ?? []) as { path?: (string | number)[]; message?: string }[];
  for (const i of issues) {
    const field = i.path?.[0];
    if (typeof field === 'string' && field in LIMITS && i.message) out[field as BusinessField] ??= i.message;
  }
  if (!Object.keys(out).length && /NIPT|tax number/i.test(err.message)) out.tax_id = err.message;
  return out;
}

/** Paper widths the printers take (Manage → Printers). */
export type PaperWidth = '80' | '58';

/** A receipt made up from the form, so the manager sees the result while typing. Never sent anywhere. */
export function sampleReceipt(f: BusinessForm, at = new Date(), rate = 20): Receipt {
  const now = at.toISOString();
  const tax = Math.round((114000 * rate) / (100 + rate));
  const p = toPayload(f);
  return {
    kind: 'receipt',
    copy: false,
    printed_at: now,
    company: {
      id: null,
      legal_name: p.legal_name,
      trade_name: p.trade_name || null,
      tax_id: p.tax_id || null,
      address: p.address || null,
      city: p.city || null,
      postcode: p.postcode || null,
      country: p.country,
      phone: p.phone || null,
      email: null,
      website: null,
      receipt_footer: p.receipt_footer || null,
      language: p.language === 'sq' ? 'sq' : 'en',
    },
    order: { id: '', number: 42, name: t('business.sample_table'), channel: 'dine_in', guests: 2, opened_at: now, closed_at: now, receipt_no: 128 },
    served_by: t('business.sample_waiter'),
    cashier: t('business.sample_waiter'),
    lines: [
      { name: t('business.sample_item_1'), qty: 2, unit_price: 12000, modifiers: [], note: null, discount: 0, total: 24000, tax_rate: rate },
      { name: t('business.sample_item_2'), qty: 1, unit_price: 90000, modifiers: [], note: null, discount: 0, total: 90000, tax_rate: rate },
    ],
    vat: [{ rate, gross: 114000, tax, net: 114000 - tax }],
    subtotal: 114000,
    discount: 0,
    total: 114000,
    tax,
    tip: 0,
    payments: [{ method: 'cash', amount: 114000, received: 120000, change: 6000 }],
    change: 6000,
    prints: 0,
    fiscal: null,
    refund: null,
  };
}

/** Every country by its two-letter code (ISO 3166-1), for the country list. */
export const COUNTRY_CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(
    ' ',
  );

/** The country list in the screen's language, sorted by name; a saved code missing from it is kept. */
export function countryOptions(locale: string, current: string): { code: string; name: string }[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    names = null;
  }
  const codes = new Set(COUNTRY_CODES);
  const cur = current.trim().toUpperCase();
  if (cur) codes.add(cur);
  return [...codes]
    .map((code) => ({ code, name: names?.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/** A country's name in the screen's language ("Italy", "Italia"), or its code. */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([locale()], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}
